import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { 
  useLoadSubjectStats, 
  useReportCardSubjectProblemsData,
  useSectionConceptSummaryReports
} from "../hooks/useReportCardData";
import MainContainer from "../components/Containers/MainContainer";
import ContentContainer from "../components/Containers/ContentContainer";
import CardContainer from "../components/Containers/CardContainer";
import Button from "../components/Buttons/Button";
import CustomDropdown from "../components/Dropdowns/CustomDropdown";
import { Users, BookOpen, Globe, Hash, FileText, ArrowLeft } from "lucide-react";
import StatsCard from "../components/Cards/StatsCard";
import LoadingSpinner from "../components/LoadingSpinner";

const UpskillReportCardForOwner = () => {
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedMedium, setSelectedMedium] = useState(null);
  const [chapter, setChapter] = useState(null);
  const [section, setSection] = useState(1);
  const [userId, setUserId] = useState(0);
  const [userData, setUserData] = useState(null);
  
  const location = useLocation();
  const navigate = useNavigate();

  const levelOptions = [{ value: "6", label: "ষষ্ঠ শ্রেণী" }];
  const subjectOptions = [{ value: "math", label: "গণিত" }];
  const mediumOptions = [{ value: "bengali", label: "বাংলা মাধ্যম" }];

  useEffect(() => {
    // Get user data from location state
    if (location.state && location.state.userData) {
      setUserData(location.state.userData);
      // Set userId from the user data instead of localStorage
      if (location.state.userData.user_id) {
        setUserId(location.state.userData.user_id);
      } else if (location.state.userData.id) {
        setUserId(location.state.userData.id);
      }
    } else {
      // Fallback to localStorage if no state data
      const storedInfo = localStorage.getItem("upskill_user_info");
      if (storedInfo !== null) {
        const userInfo = JSON.parse(storedInfo);
        setUserId(userInfo);
      }
    }
  }, [location.state]);

  // Load subject problems data
  const {
    data: subjectProblemsData,
    isLoading: isSubjectProblemsLoading,
    isError: isSubjectProblemsError,
    error: subjectProblemsError,
  } = useReportCardSubjectProblemsData(userId, selectedLevel, selectedSubject, selectedMedium);

  const { subjectProblemList, chapterKeys } = subjectProblemsData || {};

  // Set initial chapter when data is loaded
  useEffect(() => {
    if (chapterKeys && chapterKeys.length > 0) {
      setChapter(chapterKeys[0]);
      setSection(1);
    }
  }, [chapterKeys]);

  // Handler functions for chapter and section changes
  const handleChapterChange = (newChapter) => {
    setChapter(newChapter);
    setSection(1); // Reset to first section
  };

  const handleSectionChange = (newSection) => {
    setSection(newSection);
  };

  // Generate dropdown options
  const getChapterOptions = () => {
    return chapterKeys?.map((key) => ({
      value: key,
      label: `অধ্যায় ${key}`,
    })) || [];
  };

  const getSectionOptions = () => {
    if (!subjectProblemList || !chapter || !subjectProblemList[chapter]) return [];
    return Array.from(
      { length: subjectProblemList[chapter].length },
      (_, i) => ({
        value: i + 1,
        label: `পরিচ্ছেদ ${i + 1}`,
      })
    );
  };

  const {
    data: subjectStats,
    isLoading: isStatsLoading,
    isError: isStatsError,
    error: statsError,
    refetch: refetchSubjectStats,
  } = useLoadSubjectStats(
    userId,
    selectedLevel,
    selectedSubject,
    selectedMedium
  );

  const {
    data: sectionConceptSummaryData,
    isLoading: isSectionConceptLoading,
    isError: isSectionConceptError,
    error: sectionConceptError,
    refetch: fetchSectionConceptSummaryData,
  } = useSectionConceptSummaryReports(
    userId,
    selectedLevel,
    selectedSubject,
    selectedMedium,
    chapter,
    section
  );

  const handleBackToOwner = () => {
    navigate('/owner/landing');
  };

  return (
    <MainContainer>
      <ContentContainer>
        {/* Header with back button and user info */}
        <CardContainer className="p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={handleBackToOwner}
              className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Owner Dashboard
            </button>
            <h1 className="text-2xl font-bold text-gray-900">User Report Card</h1>
          </div>
          
          {userData && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">User Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(userData)
                  .filter(([key]) => key.toLowerCase() !== 'id')
                  .map(([key, value]) => (
                    <div key={key} className="flex flex-col">
                      <span className="text-sm font-medium text-gray-600 capitalize">
                        {key.replace(/([A-Z])/g, ' $1').trim()}:
                      </span>
                      <span className="text-gray-800 font-semibold">
                        {value !== null && value !== undefined ? value.toString() : 'N/A'}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </CardContainer>

        <CardContainer className="p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            আপনার পছন্দ নির্বাচন করুন
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                শ্রেণী
              </label>
              <CustomDropdown
                options={levelOptions}
                value={selectedLevel}
                onChange={setSelectedLevel}
                placeholder="শ্রেণী নির্বাচন করুন"
                icon={Users}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                বিষয়
              </label>
              <CustomDropdown
                options={subjectOptions}
                value={selectedSubject}
                onChange={setSelectedSubject}
                placeholder="বিষয় নির্বাচন করুন"
                icon={BookOpen}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                মাধ্যম
              </label>
              <CustomDropdown
                options={mediumOptions}
                value={selectedMedium}
                onChange={setSelectedMedium}
                placeholder="মাধ্যম নির্বাচন করুন"
                icon={Globe}
              />
            </div>
          </div>
        </CardContainer>

        {/* Loading spinner for subject problems */}
        {isSubjectProblemsLoading && (
          <CardContainer className="p-6 mb-8">
            <LoadingSpinner />
          </CardContainer>
        )}

        {/* Chapter and Section Selection */}
        {!isSubjectProblemsLoading && !isSubjectProblemsError && chapter && (
          <CardContainer className="p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              নির্বাচন করুন
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  অধ্যায়
                </label>
                <CustomDropdown
                  options={getChapterOptions()}
                  value={chapter}
                  onChange={handleChapterChange}
                  placeholder="অধ্যায় নির্বাচন করুন"
                  icon={Hash}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  পরিচ্ছেদ
                </label>
                <CustomDropdown
                  options={getSectionOptions()}
                  value={section}
                  onChange={handleSectionChange}
                  placeholder="পরিচ্ছেদ নির্বাচন করুন"
                  icon={FileText}
                />
              </div>
            </div>

            <div className="flex justify-center items-center w-full mt-6">
              <Button
                onClick={fetchSectionConceptSummaryData}
                disabled={
                  !selectedLevel ||
                  !selectedSubject ||
                  !selectedMedium ||
                  !chapter ||
                  !section ||
                  isSectionConceptLoading
                }
                backgroundColor={isSectionConceptLoading ? "bg-gray-400" : "bg-green-600"}
                hoverBackgroundColor={
                  isSectionConceptLoading ? "bg-gray-500" : "bg-green-700"
                }
                textColor="text-white"
                text={isSectionConceptLoading ? "লোড হচ্ছে..." : "সেকশন রিপোর্ট দেখুন"}
              />
            </div>
          </CardContainer>
        )}

        {/* Section Concept Summary Table */}
        {sectionConceptSummaryData && sectionConceptSummaryData.length > 0 && (
          <CardContainer className="p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              সেকশন কনসেপ্ট রিপোর্ট
            </h3>
            
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                      কনসেপ্ট
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                      সম্পূর্ণ
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                      ব্যবহারকারীর উত্তর
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                      স্কিল রিপোর্ট
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                      জমা দেওয়ার সময়
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sectionConceptSummaryData.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.concept}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          item.completed === 1 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {item.completed === 1 ? 'সম্পূর্ণ' : 'অসম্পূর্ণ'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.user_response}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.skill_report}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(item.submission_time).toLocaleDateString('bn-BD')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContainer>
        )}

        {/* Report Statistics */}
        <CardContainer className="p-6 mb-8">
          {subjectStats && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                রিপোর্ট পরিসংখ্যান
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <StatsCard title="সঠিক উত্তর" content={subjectStats[1]} />
                <StatsCard title="মোট প্রশ্ন" content={subjectStats[0]} />
                <StatsCard title="শতকরা হার" content={`${subjectStats[2]}%`} />
              </div>
            </div>
          )}

          <div className="flex justify-center items-center w-full">
            <Button
              onClick={refetchSubjectStats}
              disabled={
                !selectedLevel ||
                !selectedSubject ||
                !selectedMedium ||
                isStatsLoading
              }
              backgroundColor={isStatsLoading ? "bg-gray-400" : "bg-blue-600"}
              hoverBackgroundColor={
                isStatsLoading ? "bg-gray-500" : "bg-blue-700"
              }
              textColor="text-white"
              text={isStatsLoading ? "লোড হচ্ছে..." : "রিপোর্ট দেখুন"}
            />
          </div>
        </CardContainer>
      </ContentContainer>
    </MainContainer>
  );
};

export default UpskillReportCardForOwner;

